import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import NameplateScanDialog from './NameplateScanDialog';
import BulkImportDialog from './BulkImportDialog';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../ui/alert-dialog';
import { Camera, Edit, Plus, Trash2, Upload } from 'lucide-react';
import useDeepShortcut from '../../hooks/useDeepShortcut';
import PermButton from '../shared/PermButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  name: '', manufacturer: '', category_id: '', asset_type: 'tangible',
  inspection_interval_months: '', pat_required: false, useful_life_years: '',
  manual_link: '', part_number: '', notes: '',
};

/**
 * The catalogue behind quick-add. "Hilti TE 60-ATC" is entered once with its
 * category, inspection interval, PAT requirement and manual link; after that
 * registering the fifth one on site is two dropdowns and a serial number.
 */
const AssetModelsRegister = ({ models = [], manufacturers = [], categories = [], onRefresh }) => {
  const { t } = useTranslation();
  const [scanOpen, setScanOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  // The picture endpoint is behind auth, so a bare <img src> gets a 401.
  // Fetch each thumbnail once and hand the <img> a blob URL instead.
  const [thumbs, setThumbs] = useState({});
  const thumbsLoaded = useRef(new Set());

  useEffect(() => {
    models.forEach(async (m) => {
      if (!m.default_picture_gridfs_id || thumbsLoaded.current.has(m.id)) return;
      thumbsLoaded.current.add(m.id);
      try {
        const r = await axios.get(`${API}/inventory/asset-models/${m.id}/picture`, { responseType: 'blob' });
        setThumbs((prev) => ({ ...prev, [m.id]: URL.createObjectURL(r.data) }));
      } catch { thumbsLoaded.current.delete(m.id); }
    });
  }, [models]);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [deleteId, setDeleteId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingFor, setUploadingFor] = useState(null);
  const fileRef = useRef(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Dashboard shortcuts: an open catalogue entry, or the New model form.
  useDeepShortcut({
    active: formOpen && !!editing,
    tab: 'inventory',
    context: { invSubTab: 'catalogue', focusModelId: editing?.id },
    label: editing?.name,
    subtitle: t('inv.tabs.catalogue'),
    icon: 'catalogue',
    perms: ['inventory_catalogue.view'],
    priority: 3,
    focusKey: 'focusModelId',
    onFocus: (id) => {
      const found = models.find((m) => m.id === id);
      if (found) openEdit(found);
    },
  });

  const openCreate = () => { setForm(EMPTY); setEditing(null); setFormOpen(true); };
  const openEdit = (m) => {
    setForm({
      name: m.name || '', manufacturer: m.manufacturer || '', category_id: m.category_id || '',
      asset_type: m.asset_type || 'tangible',
      inspection_interval_months: m.inspection_interval_months ?? '',
      pat_required: !!m.pat_required,
      useful_life_years: m.useful_life_years ?? '',
      manual_link: m.manual_link || '', part_number: m.part_number || '', notes: m.notes || '',
    });
    setEditing(m);
    setFormOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error(t('inv.models.nameRequired')); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        manufacturer: form.manufacturer || null,
        category_id: form.category_id || null,
        inspection_interval_months: form.inspection_interval_months === '' ? null : parseInt(form.inspection_interval_months, 10),
        useful_life_years: form.useful_life_years === '' ? null : parseFloat(form.useful_life_years),
        manual_link: form.manual_link || null,
        part_number: form.part_number || null,
        notes: form.notes || null,
        specs: editing?.specs || [],
        source_url: editing?.source_url || null,
        plate_photo_gridfs_id: editing?.plate_photo_gridfs_id || null,
        year_of_manufacture: editing?.year_of_manufacture || null,
      };
      if (editing) await axios.put(`${API}/inventory/asset-models/${editing.id}`, payload);
      else await axios.post(`${API}/inventory/asset-models`, payload);
      toast.success(t(editing ? 'inv.models.updated' : 'inv.models.added'));
      setFormOpen(false);
      onRefresh?.();
    } catch (err) { toast.error(err.response?.data?.detail || t('inv.common.saveFailed')); }
    setSaving(false);
  };

  const remove = async () => {
    try {
      await axios.delete(`${API}/inventory/asset-models/${deleteId}`);
      toast.success(t('inv.models.deleted'));
      setDeleteId(null);
      onRefresh?.();
    } catch (err) { toast.error(err.response?.data?.detail || t('inv.common.deleteFailed')); }
  };

  const pickPhoto = (model) => { setUploadingFor(model); fileRef.current?.click(); };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !uploadingFor) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      await axios.post(`${API}/inventory/asset-models/${uploadingFor.id}/picture`, fd);
      toast.success(t('inv.models.photoSaved'));
      onRefresh?.();
    } catch (err) { toast.error(err.response?.data?.detail || t('inv.common.uploadFailed')); }
    setUploadingFor(null);
  };

  return (
    <Card data-testid="asset-models-card">
      <CardHeader className="flex flex-col gap-2 pb-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <CardTitle className="text-base">{t('inv.models.title', { count: models.length })}</CardTitle>
          <p className="mt-0.5 text-xs text-muted-foreground">{t('inv.models.hint')}</p>
        </div>
        <div className="flex flex-wrap gap-2 sm:shrink-0">
          <PermButton perm="inventory_catalogue.create" size="sm" variant="outline"
            onClick={() => setImportOpen(true)} data-testid="import-models-btn">
            <Upload className="mr-1 h-4 w-4" /> {t('inv.import.button')}
          </PermButton>
          <PermButton perm="inventory_catalogue.create" size="sm" variant="outline"
            onClick={() => setScanOpen(true)} data-testid="scan-nameplate-btn">
            <Camera className="mr-1 h-4 w-4" /> {t('inv.nameplate.scanButton')}
          </PermButton>
          <PermButton perm="inventory_catalogue.create" size="sm" onClick={openCreate}
            data-testid="add-asset-model-btn">
            <Plus className="mr-1 h-4 w-4" /> {t('common.add')}
          </PermButton>
        </div>
      </CardHeader>
      <CardContent>
        {models.length === 0 ? (
          <p className="py-4 text-center text-sm text-muted-foreground">{t('inv.models.empty')}</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('inv.assets.model')}</TableHead>
                <TableHead className="hidden sm:table-cell">{t('inv.assets.manufacturer')}</TableHead>
                <TableHead className="hidden md:table-cell">{t('inv.common.category')}</TableHead>
                <TableHead className="hidden lg:table-cell">{t('inv.models.defaults')}</TableHead>
                <TableHead className="w-[110px]">{t('common.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {models.map((m) => (
                <TableRow key={m.id} data-testid={`asset-model-row-${m.name}`}>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      {thumbs[m.id] && (
                        <img src={thumbs[m.id]} alt=""
                          className="h-8 w-8 rounded border object-cover"
                          data-testid={`model-thumb-${m.name}`} />
                      )}
                      <span className="break-words">{m.name}</span>
                    </div>
                    <span className="block text-xs text-muted-foreground sm:hidden">{m.manufacturer || ''}</span>
                  </TableCell>
                  <TableCell className="hidden sm:table-cell text-sm">{m.manufacturer || '-'}</TableCell>
                  <TableCell className="hidden md:table-cell text-sm">{m.category_name || '-'}</TableCell>
                  <TableCell className="hidden lg:table-cell">
                    <div className="flex flex-wrap gap-1">
                      {m.inspection_interval_months ? <Badge variant="secondary" className="text-[10px]">{t('inv.models.inspEvery', { count: m.inspection_interval_months })}</Badge> : null}
                      {m.pat_required ? <Badge variant="secondary" className="text-[10px]">{t('auto.inv.pat')}</Badge> : null}
                      {m.useful_life_years ? <Badge variant="outline" className="text-[10px]">{t('inv.models.yrLife', { count: m.useful_life_years })}</Badge> : null}
                      {(m.specs || []).length > 0 && (
                        m.source_url ? (
                          <a href={m.source_url} target="_blank" rel="noreferrer"
                            data-testid={`model-specs-${m.name}`}>
                            <Badge variant="outline" className="border-blue-200 bg-blue-50 text-[10px] text-blue-800">
                              {t('inv.models.specCount', { count: m.specs.length })}
                            </Badge>
                          </a>
                        ) : (
                          <Badge variant="outline" className="text-[10px]" data-testid={`model-specs-${m.name}`}>
                            {t('inv.models.specCount', { count: m.specs.length })}
                          </Badge>
                        )
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <PermButton perm="inventory_catalogue.edit" variant="ghost" size="icon" className="h-7 w-7"
                        title={t('inv.models.cataloguePhoto')}
                        onClick={() => pickPhoto(m)} data-testid={`model-photo-${m.name}`}>
                        <Camera className="h-3.5 w-3.5" />
                      </PermButton>
                      <PermButton perm="inventory_catalogue.edit" variant="ghost" size="icon" className="h-7 w-7"
                        onClick={() => openEdit(m)} data-testid={`edit-asset-model-${m.name}`}>
                        <Edit className="h-3.5 w-3.5" />
                      </PermButton>
                      <PermButton perm="inventory_catalogue.delete" variant="ghost" size="icon" className="h-7 w-7 text-red-500"
                        onClick={() => setDeleteId(m.id)} data-testid={`delete-asset-model-${m.name}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </PermButton>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadPhoto} />

      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-lg" data-testid="asset-model-form-dialog">
          <DialogHeader>
            <DialogTitle>{t(editing ? 'inv.models.editModel' : 'inv.models.addModel')}</DialogTitle>
            <DialogDescription className="sr-only">{t('inv.models.catalogueEntry')}</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <Label className="text-xs">{t('inv.models.modelName')} *</Label>
                <Input value={form.name} onChange={(e) => set('name', e.target.value)} required
                  placeholder={t('auto.inv.te60Atc')} data-testid="model-name-input" />
              </div>
              <div>
                <Label className="text-xs">{t('inv.assets.manufacturer')}</Label>
                {manufacturers.length > 0 ? (
                  <Select value={form.manufacturer} onValueChange={(v) => set('manufacturer', v)}>
                    <SelectTrigger data-testid="model-manufacturer-select"><SelectValue placeholder={t('inv.common.select')} /></SelectTrigger>
                    <SelectContent>
                      {manufacturers.map((m) => <SelectItem key={m.id} value={m.name}>{m.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input value={form.manufacturer} onChange={(e) => set('manufacturer', e.target.value)}
                    data-testid="model-manufacturer-select" />
                )}
              </div>
              <div>
                <Label className="text-xs">{t('inv.common.category')}</Label>
                <Select value={form.category_id} onValueChange={(v) => set('category_id', v)}>
                  <SelectTrigger data-testid="model-category-select"><SelectValue placeholder={t('inv.common.select')} /></SelectTrigger>
                  <SelectContent>
                    {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">{t('inv.form.assetType')}</Label>
                <Select value={form.asset_type} onValueChange={(v) => set('asset_type', v)}>
                  <SelectTrigger data-testid="model-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tangible">{t('inv.assets.type.tangible')}</SelectItem>
                    <SelectItem value="intangible">{t('inv.assets.type.intangible')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">{t('inv.assets.partNumber')}</Label>
                <Input value={form.part_number} onChange={(e) => set('part_number', e.target.value)} />
              </div>
              <div>
                <Label className="text-xs">{t('inv.models.inspectionInterval')}</Label>
                <Input type="number" min="0" value={form.inspection_interval_months}
                  onChange={(e) => set('inspection_interval_months', e.target.value)}
                  data-testid="model-interval-input" />
              </div>
              <div>
                <Label className="text-xs">{t('inv.form.usefulLifeYears')}</Label>
                <Input type="number" step="0.5" min="0" value={form.useful_life_years}
                  onChange={(e) => set('useful_life_years', e.target.value)} />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs">{t('inv.form.manualLink')}</Label>
                <Input value={form.manual_link} onChange={(e) => set('manual_link', e.target.value)}
                  placeholder={t('auto.inv.https2')} />
              </div>
              <label className="flex cursor-pointer select-none items-center gap-2 text-sm sm:col-span-2">
                <input type="checkbox" className="h-4 w-4 accent-[#AA0000]" checked={form.pat_required}
                  onChange={(e) => set('pat_required', e.target.checked)} data-testid="model-pat-required" />
                {t('auto.inv.needsPatTesting')}
              </label>
              <div className="sm:col-span-2">
                <Label className="text-xs">{t('common.notes')}</Label>
                <Textarea rows={2} value={form.notes} onChange={(e) => set('notes', e.target.value)} />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setFormOpen(false)}>{t('common.cancel')}</Button>
              <Button type="submit" disabled={saving} data-testid="save-asset-model-btn">
                {saving ? t('inv.common.saving') : editing ? t('inv.common.update') : t('common.add')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('inv.models.deleteTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('inv.models.deleteHint')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-red-600 hover:bg-red-700">{t('common.delete')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <NameplateScanDialog open={scanOpen} onOpenChange={setScanOpen} categories={categories}
        onSaved={() => onRefresh?.()} />

      <BulkImportDialog open={importOpen} onOpenChange={setImportOpen} kind="models"
        onImported={() => onRefresh?.()} />
    </Card>
  );
};

export default AssetModelsRegister;
